import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Box, IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogContentText,
  DialogActions, FormControlLabel, Checkbox } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CheckIcon from '@mui/icons-material/Check';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import CloseIcon from '@mui/icons-material/Close';
import { useHistory, useRouteMatch } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import InvDataGrid from '../../components/InvDataGrid';
import StatusChip from '../../components/StatusChip';
import InvButton from '../../components/InvButton';
import ConfirmDialog from '../../components/ConfirmDialog';
import FilterToolbar from '../../components/FilterToolbar';
import { getReturns, confirmReturn, acknowledgeReturn, rejectReturn } from '../../api/dispensing';
import { extractDispensingScope } from '../../utils/dispensingEntity';
import { formatDate } from '../../utils';
import { RETURN_TYPES } from '../../constants';
import useInvRole from '../../hooks/useInvRole';
import useActiveStore from '../../hooks/useActiveStore';
import { INV_ACTIONS } from '../../utils/inventoryRoles';
import { IPD_COLORS, cardSx } from '../../theme';

// The returns-list endpoint does not document filter query params, so status/type
// filtering is done client-side over the loaded rows.
const RETURN_STATUSES = [
  'DRAFT', 'CONFIRMED', 'RETURNED', 'PARTIALLY_RETURNED', 'ACKNOWLEDGED', 'REJECTED',
];

const RETURN_TYPE_LABELS = Object.fromEntries(RETURN_TYPES.map(t => [t.value, t.label]));

/** List payloads may nest return_type under transaction_payload (same as pharmacy inbox). */
function resolveReturnType(row) {
  return row?.return_type || row?.transaction_payload?.return_type || '';
}

function formatReturnType(value) {
  if (!value) return '—';
  return RETURN_TYPE_LABELS[value] || String(value).replace(/_/g, ' ');
}

const ACTION_SLOT_SX = {
  width: 32,
  height: 32,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
};

export default function ReturnList() {
  const { url } = useRouteMatch();
  const history = useHistory();
  const { can } = useInvRole();
  const { isStoreScoped, activeStoreId } = useActiveStore();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [search, setSearch] = useState('');
  const [confirmTarget, setConfirmTarget] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [ackTarget, setAckTarget] = useState(null);
  const [ackTriggerReversal, setAckTriggerReversal] = useState(false);
  const [acking, setAcking] = useState(false);
  const [dialog, setDialog] = useState(null);
  const [reason, setReason] = useState('');
  const [actioning, setActioning] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      // A store-scoped user (Store Keeper) sees only their own store. One param because
      // their store sits on both sides of a return, and the backend owns that semantic.
      // NOTE: this endpoint's filter params are undocumented (see the note above the
      // status/type client-side filtering) — confirm it honours store_id.
      // API: GET /inventory/dispensing/returns/
      const data = await getReturns(isStoreScoped && activeStoreId ? { store_id: activeStoreId } : undefined);
      setItems(Array.isArray(data) ? data : (data?.items || []));
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [isStoreScoped, activeStoreId]);

  useEffect(() => { load(); }, [load]);

  const filteredItems = useMemo(() => {
    const term = search.trim().toLowerCase();
    return items.filter(r => {
      if (statusFilter && r.status !== statusFilter) return false;
      if (typeFilter && resolveReturnType(r) !== typeFilter) return false;
      if (term && !`${r.transaction_number || ''} ${r.remarks || ''}`.toLowerCase().includes(term)) return false;
      return true;
    });
  }, [items, statusFilter, typeFilter, search]);

  // Fixed chip width from longest known/data label so every status badge aligns.
  // Char width accounts for INV_TYPE.chip (11px / letterSpacing 0.3).
  const statusChipMinWidth = useMemo(() => {
    const fromData = items
      .map(r => (r.status ? String(r.status).replace(/_/g, ' ') : ''))
      .filter(Boolean);
    const labels = [...new Set([
      ...RETURN_STATUSES.map(s => s.replace(/_/g, ' ')),
      ...fromData,
    ])];
    return Math.ceil(Math.max(...labels.map(l => l.length)) * 8 + 24);
  }, [items]);

  const statusColumnWidth = Math.max(180, statusChipMinWidth + 28);

  async function doConfirm() {
    if (!can(INV_ACTIONS.RETURN_CONFIRM)) return;
    setConfirming(true);
    try {
      // API: POST /inventory/dispensing/returns/{id}/confirm/
      await confirmReturn(confirmTarget.transaction_id || confirmTarget.id, {
        trigger_billing_reversal: false, remarks: '', ...extractDispensingScope(confirmTarget),
      });
      setConfirmTarget(null); load();
    } catch (e) {
      console.error('[ReturnList] confirm failed:', e);
      setError(e.message);
    } finally { setConfirming(false); }
  }

  async function doAcknowledge() {
    if (!can(INV_ACTIONS.RETURN_ACKNOWLEDGE)) return;
    setAcking(true);
    try {
      // API: POST /inventory/dispensing/returns/{id}/acknowledge/
      await acknowledgeReturn(ackTarget.transaction_id || ackTarget.id, {
        trigger_billing_reversal: ackTriggerReversal, ...extractDispensingScope(ackTarget),
      });
      setAckTarget(null); load();
    } catch (e) {
      console.error('[ReturnList] acknowledge failed:', e);
      setError(e.message);
    } finally { setAcking(false); }
  }

  async function executeAction() {
    if (dialog.action === 'reject' && !can(INV_ACTIONS.RETURN_REJECT)) return;
    setActioning(true);
    try {
      const row = dialog.target;
      const id = row.transaction_id || row.id;
      const scope = extractDispensingScope(row);
      // API: POST /inventory/dispensing/returns/{id}/reject/ — reason required
      if (dialog.action === 'reject') await rejectReturn(id, { reason, ...scope });
      setDialog(null); setReason(''); load();
    } catch (e) {
      console.error('[ReturnList] action failed:', e);
      setError(e.message);
    } finally { setActioning(false); }
  }

  const columns = useMemo(() => [
    {
      field: 'transaction_number',
      headerName: 'Return #',
      width: 210,
      renderCell: p => (
        <Box
          component="span"
          title={p.value || undefined}
          sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
        >
          {p.value || '—'}
        </Box>
      ),
    },
    {
      field: 'return_type',
      headerName: 'Type',
      width: 160,
      valueGetter: p => resolveReturnType(p.row),
      valueFormatter: p => formatReturnType(p.value),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: statusColumnWidth,
      align: 'center',
      headerAlign: 'center',
      renderCell: p => (p.value
        ? <StatusChip status={p.value} minWidth={statusChipMinWidth} />
        : '—'),
    },
    {
      field: 'transaction_date',
      headerName: 'Date',
      width: 120,
      valueFormatter: p => formatDate(p.value),
    },
    {
      field: 'actions',
      headerName: '',
      width: 120,
      sortable: false,
      renderCell: p => {
        const showConfirm = p.row.status === 'DRAFT' && can(INV_ACTIONS.RETURN_CONFIRM);
        const showAck = p.row.status === 'RETURNED' && can(INV_ACTIONS.RETURN_ACKNOWLEDGE);
        const showReject = p.row.status === 'RETURNED' && can(INV_ACTIONS.RETURN_REJECT);
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
            <Box sx={ACTION_SLOT_SX}>
              <Tooltip title="View">
                <IconButton
                  size="small"
                  onClick={() => history.push(`${url}/${p.row.transaction_id || p.row.id}`)}
                  sx={{ color: IPD_COLORS.primary }}
                >
                  <VisibilityIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            <Box sx={ACTION_SLOT_SX}>
              {showConfirm && (
                <Tooltip title="Confirm">
                  <IconButton
                    size="small"
                    onClick={() => setConfirmTarget(p.row)}
                    sx={{ color: IPD_COLORS.success.text }}
                  >
                    <CheckIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              {showAck && (
                <Tooltip title="Acknowledge">
                  <IconButton
                    size="small"
                    onClick={() => { setAckTarget(p.row); setAckTriggerReversal(false); }}
                    sx={{ color: IPD_COLORS.success.text }}
                  >
                    <DoneAllIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
            <Box sx={ACTION_SLOT_SX}>
              {showReject && (
                <Tooltip title="Reject">
                  <IconButton
                    size="small"
                    onClick={() => setDialog({
                      action: 'reject',
                      target: p.row,
                      title: 'Reject Return',
                      desc: 'Reject this return?',
                      destructive: true,
                      requireReason: true,
                    })}
                    sx={{ color: IPD_COLORS.error.text }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </Box>
        );
      },
    },
  ], [url, history, can, statusChipMinWidth, statusColumnWidth]);

  return (
    <Box>
      {/* No store column on this grid, so the scope is stated in the description. */}
      <PageHeader eyebrow="Inventory Management" title="Returns"
        description={`${filteredItems.length} of ${items.length} returns${isStoreScoped ? ' · your store only' : ''}`}
        actions={can(INV_ACTIONS.RETURN_CREATE)
          ? <InvButton startIcon={<AddIcon />} onClick={() => history.push(`${url.replace('/returns', '')}/return/new`)}>New Return</InvButton>
          : null}
      />
      <FilterToolbar>
        <FilterToolbar.Filter
          label="Status"
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          options={[
            { value: '', label: 'All' },
            ...RETURN_STATUSES.map(s => ({ value: s, label: s.replace(/_/g, ' ') })),
          ]}
        />
        <FilterToolbar.Filter
          label="Return Type"
          width="wide"
          value={typeFilter}
          onChange={e => setTypeFilter(e.target.value)}
          options={[{ value: '', label: 'All' }, ...RETURN_TYPES]}
        />
        <FilterToolbar.Search
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Return / remarks"
        />
      </FilterToolbar>
      <InvDataGrid rows={filteredItems} columns={columns} loading={loading} error={error} onRetry={load}
        getRowId={r => r.transaction_id || r.id} emptyMessage="No returns found."
        emptyAction={can(INV_ACTIONS.RETURN_CREATE)
          ? <InvButton startIcon={<AddIcon />} onClick={() => history.push(`${url.replace('/returns', '')}/return/new`)}>New Return</InvButton>
          : null} />
      <ConfirmDialog open={Boolean(confirmTarget)} title="Confirm Return"
        description="Confirm this return? Items will be credited back to stock."
        loading={confirming} onConfirm={doConfirm} onCancel={() => setConfirmTarget(null)} />

      {/* Acknowledge needs a billing-reversal toggle, which ConfirmDialog has no slot for. */}
      <Dialog open={Boolean(ackTarget)} onClose={acking ? undefined : () => setAckTarget(null)}
        PaperProps={{ sx: { ...cardSx, minWidth: 380, p: 1 } }}>
        <DialogTitle sx={{ color: IPD_COLORS.textPrimary }}>Acknowledge Return</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: IPD_COLORS.textSecondary }}>
            Acknowledge receipt of this return?
          </DialogContentText>
          <FormControlLabel sx={{ mt: 1, color: IPD_COLORS.textPrimary }}
            control={<Checkbox checked={ackTriggerReversal} onChange={e => setAckTriggerReversal(e.target.checked)} />}
            label="Also reverse the associated billing entry" />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <InvButton variant="outlined" tone="teal" onClick={() => setAckTarget(null)} disabled={acking}>Cancel</InvButton>
          <InvButton tone="mint" onClick={doAcknowledge} loading={acking}>Acknowledge</InvButton>
        </DialogActions>
      </Dialog>

      <ConfirmDialog open={Boolean(dialog)} title={dialog?.title} description={dialog?.desc}
        destructive={dialog?.destructive} requireReason={dialog?.requireReason}
        reasonValue={reason} onReasonChange={setReason} loading={actioning}
        onConfirm={executeAction} onCancel={() => { setDialog(null); setReason(''); }} />
    </Box>
  );
}
